import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Star, Calendar, MapPin, Clock, ShieldCheck, Zap, Lock, Sun, Moon, CircleDot, ArrowUpRight, FileText, Heart, Check, CreditCard, RotateCcw, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { BetaReportPreview } from '../components/reports/BetaReportPreview';
import { PremiumReportPreview } from '../components/reports/PremiumReportPreview';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../i18n/LanguageContext';
import { AdSlot } from '../components/ads/AdSlot';
interface FreeChartPageProps {
  onNavigate: (page: string) => void;
}
export function FreeChartPage({
  onNavigate
}: FreeChartPageProps) {
  const {
    t
  } = useLanguage();
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState('natal');
  const [selectedPackage, setSelectedPackage] = useState<'free' | 'pro' | 'package'>('free');
  const [selectedConsultationTier, setSelectedConsultationTier] = useState<'short' | 'standard' | 'extended'>('standard');
  const [selectedBundleSize, setSelectedBundleSize] = useState<20 | 50 | 100>(50);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationComplete, setCalculationComplete] = useState(false);
  const {
    addItem
  } = useCart();
  const [formData, setFormData] = useState({
    p1: {
      name: '',
      date: '',
      time: '',
      city: ''
    },
    p2: {
      name: '',
      date: '',
      time: '',
      city: ''
    },
    year: new Date().getFullYear().toString(),
    period: '3'
  });
  const SERVICES = [{
    id: 'natal',
    title: t('services.natal'),
    desc: t('services.natal.desc'),
    icon: Star,
    color: 'text-indigo-500',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    activeBg: 'bg-indigo-500/10',
    activeBorder: 'border-indigo-500',
    price: 3900
  }, {
    id: 'solar',
    title: t('services.solar'),
    desc: t('services.solar.desc'),
    icon: Sun,
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    activeBg: 'bg-amber-500/10',
    activeBorder: 'border-amber-500',
    price: 4400
  }, {
    id: 'compatibility',
    title: t('services.compatibility'),
    desc: t('services.compatibility.desc'),
    icon: Heart,
    color: 'text-rose-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    activeBg: 'bg-rose-500/10',
    activeBorder: 'border-rose-500',
    price: 4900
  }, {
    id: 'combo',
    title: t('services.combo'),
    desc: t('services.combo.desc'),
    icon: FileText,
    color: 'text-slate-500',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    activeBg: 'bg-slate-500/10',
    activeBorder: 'border-slate-500',
    price: 6900
  }, {
    id: 'forecast',
    title: t('services.forecast'),
    desc: t('services.forecast.desc'),
    icon: Calendar,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    activeBg: 'bg-emerald-500/10',
    activeBorder: 'border-emerald-500',
    price: 3900
  }, {
    id: 'consultation',
    title: t('services.consultation'),
    desc: t('services.consultation.desc'),
    icon: MessageCircle,
    color: 'text-sky-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    activeBg: 'bg-sky-500/10',
    activeBorder: 'border-sky-500',
    price: 4990
  }];
  const CONSULTATION_TIERS = [{
    id: 'short' as const,
    title: t('chart.consult.short.title'),
    desc: t('chart.consult.short.desc'),
    price: 2990,
    badge: t('chart.consult.short.badge'),
    badgeVariant: 'neutral' as const
  }, {
    id: 'standard' as const,
    title: t('chart.consult.standard.title'),
    desc: t('chart.consult.standard.desc'),
    price: 4990,
    badge: t('chart.consult.standard.badge'),
    badgeVariant: 'pro' as const,
    popular: true
  }, {
    id: 'extended' as const,
    title: t('chart.consult.extended.title'),
    desc: t('chart.consult.extended.desc'),
    price: 7990,
    badge: t('chart.consult.extended.badge'),
    badgeVariant: 'success' as const
  }];
  const STEPS = [{
    id: 1,
    title: t('chart.step.service')
  }, {
    id: 2,
    title: t('chart.step.package')
  }, {
    id: 3,
    title: t('chart.step.data')
  }, {
    id: 4,
    title: t('chart.step.result')
  }];
  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [step]);
  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };
  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
  };
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedService === 'consultation') {
      const tier = CONSULTATION_TIERS.find(t => t.id === selectedConsultationTier) || CONSULTATION_TIERS[1];
      addItem({
        id: `consultation-${selectedConsultationTier}-${Date.now()}`,
        name: tier.title,
        description: `${tier.desc} • Живой разбор с профессиональным астрологом`,
        price: tier.price,
        type: 'consultation',
        format: 'Видео / Аудио',
        duration: tier.desc.split('•')[0].trim(),
        badges: [{
          label: 'Консультация',
          variant: 'pro'
        }]
      });
      onNavigate('checkout');
      return;
    }
    if (selectedPackage === 'package') {
      const bundlePrice = selectedBundleSize === 20 ? 19900 : selectedBundleSize === 50 ? 44900 : 79900;
      addItem({
        id: `pkg-${selectedBundleSize}-${Date.now()}`,
        name: `Пакет ${selectedBundleSize} отчётов`,
        description: `${selectedBundleSize} Premium-отчётов для любых услуг платформы`,
        price: bundlePrice,
        type: 'package',
        format: 'PDF-отчёты',
        duration: 'Бессрочно',
        badges: [{
          label: 'Пакет отчётов',
          variant: 'success'
        }]
      });
      onNavigate('checkout');
      return;
    }
    setStep(4);
    setIsCalculating(true);
    setCalculationComplete(false);
    // Simulate calculation delay
    setTimeout(() => {
      setIsCalculating(false);
      setCalculationComplete(true);
    }, 2000);
  };
  const resetWizard = () => {
    setStep(1);
    setCalculationComplete(false);
    setIsCalculating(false);
  };
  const currentService = SERVICES.find(s => s.id === selectedService) || SERVICES[0];
  // Mock planet data for the results table
  const planetPositions = [{
    name: t('chart.planet.sun'),
    icon: <Sun className="w-4 h-4 text-amber-500" data-id="element-21333" />,
    sign: t('chart.sign.aries'),
    degree: "15° 24'"
  }, {
    name: t('chart.planet.moon'),
    icon: <Moon className="w-4 h-4 text-slate-400" data-id="element-21334" />,
    sign: t('chart.sign.taurus'),
    degree: "03° 12'"
  }, {
    name: t('chart.planet.mercury'),
    icon: <CircleDot className="w-4 h-4 text-emerald-500" data-id="element-21335" />,
    sign: t('chart.sign.pisces'),
    degree: "28° 45'"
  }, {
    name: t('chart.planet.venus'),
    icon: <Star className="w-4 h-4 text-rose-500" data-id="element-21336" />,
    sign: t('chart.sign.taurus'),
    degree: "10° 05'"
  }, {
    name: t('chart.planet.mars'),
    icon: <ArrowUpRight className="w-4 h-4 text-red-500" data-id="element-21337" />,
    sign: t('chart.sign.gemini'),
    degree: "05° 30'"
  }, {
    name: t('chart.planet.jupiter'),
    icon: <Star className="w-4 h-4 text-indigo-500" data-id="element-21338" />,
    sign: t('chart.sign.sagittarius'),
    degree: "22° 15'"
  }, {
    name: t('chart.planet.saturn'),
    icon: <Star className="w-4 h-4 text-slate-600" data-id="element-21339" />,
    sign: t('chart.sign.capricorn'),
    degree: "14° 50'"
  }];
  const renderStepper = () => <div className="mb-8" data-id="element-21340">
      <div className="flex items-center justify-between relative" data-id="element-21341">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-100 rounded-full z-0" data-id="element-21342"></div>
        <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-indigo-500 rounded-full z-0 transition-all duration-300" style={{
        width: `${(step - 1) / (STEPS.length - 1) * 100}%`
      }} data-id="element-21343"></div>

        {STEPS.map(s => {
        const isActive = s.id === step;
        const isCompleted = s.id < step;
        return <div key={s.id} className="relative z-10 flex flex-col items-center" data-id="element-21344">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${isActive ? 'bg-indigo-600 text-white shadow-md ring-4 ring-indigo-50' : isCompleted ? 'bg-indigo-500 text-white' : 'bg-white text-slate-400 border-2 border-slate-200'}`} data-id="element-21345">
                {isCompleted ? <Check className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-21346" /> : s.id}
              </div>
              <span className={`absolute top-full mt-2 text-[10px] sm:text-xs font-medium whitespace-nowrap ${isActive ? 'text-indigo-900' : isCompleted ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-21347">
                {s.title}
              </span>
            </div>;
      })}
      </div>
    </div>;
  const renderStep1 = () => <motion.div key="step1" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="space-y-6" data-id="element-21348">
      <div className="text-center mb-6" data-id="element-21349">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-21350">
          {t('chart.step1.title')}
        </h2>
        <p className="text-slate-500 text-sm sm:text-base" data-id="element-21351">
          {t('chart.step1.subtitle')}
        </p>
      </div>

      <div className="space-y-2 max-w-2xl mx-auto" data-id="element-21352">
        {SERVICES.map(service => {
        const Icon = service.icon;
        const isActive = selectedService === service.id;
        return <button key={service.id} onClick={() => setSelectedService(service.id)} className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${isActive ? `${service.activeBorder} ${service.activeBg} shadow-sm` : 'border-slate-100 bg-white hover:border-slate-200'}`} data-id="element-21353">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${service.bg}`} data-id="element-21354">
                <Icon className={`w-4.5 h-4.5 ${service.color}`} data-id="element-21355" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-21356">
                <div className="font-semibold text-slate-900 text-sm leading-tight flex items-center gap-2" data-id="element-21357">
                  {service.title}
                  {service.id === 'natal' && <Badge variant="pro" className="text-[9px] px-1.5 py-0" data-id="element-21358">
                      {t('chart.popular')}
                    </Badge>}
                  {service.id === 'consultation' && <Badge variant="success" className="text-[9px] px-1.5 py-0" data-id="element-21359">
                      {t('chart.new')}
                    </Badge>}
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5" data-id="element-21360">
                  {service.desc}
                </div>
              </div>
              <div className="text-right flex-shrink-0 hidden sm:block" data-id="element-21361">
                <div className="text-xs font-bold text-slate-900" data-id="element-21362">
                  {service.id === 'consultation' ? t('chart.fromPrice') : t('chart.freePremium')}
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? `${service.activeBorder} ${service.activeBg}` : 'border-slate-300'}`} data-id="element-21363">
                {isActive && <div className={`w-2.5 h-2.5 rounded-full ${service.activeBorder.replace('border-', 'bg-')}`} data-id="element-21364" />}
              </div>
            </button>;
      })}
      </div>

      <div className="flex justify-end pt-6 border-t border-slate-100 mt-8" data-id="element-21365">
        <Button onClick={handleNext} variant="primary" icon={<ArrowRight className="w-4 h-4" data-id="element-21367" />} data-id="element-21366">
          {t('chart.btn.next')}
        </Button>
      </div>
    </motion.div>;
  const renderStep2 = () => {
    const CurrentIcon = currentService.icon;
    const isConsultation = selectedService === 'consultation';
    return <motion.div key="step2" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="space-y-6" data-id="element-21368">
        <div className="text-center mb-6" data-id="element-21369">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-21370">
            {isConsultation ? t('chart.step2.titleConsultation') : t('chart.step2.titleReport')}
          </h2>
          <p className="text-slate-500 text-sm sm:text-base" data-id="element-21371">
            {t('chart.step2.forService', {
            name: currentService.title
          })}
          </p>
        </div>

        <div className="max-w-2xl mx-auto space-y-6" data-id="element-21372">
          {/* Selected Service Summary */}
          <div className={`flex items-center gap-3 p-4 rounded-xl border border-slate-100 ${currentService.bg} bg-opacity-50`} data-id="element-21373">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-white shadow-sm flex-shrink-0`} data-id="element-21374">
              <CurrentIcon className={`w-5 h-5 ${currentService.color}`} data-id="element-21375" />
            </div>
            <div data-id="element-21376">
              <div className="text-sm font-bold text-slate-900" data-id="element-21377">
                {currentService.title}
              </div>
              <div className="text-xs text-slate-500" data-id="element-21378">
                {currentService.desc}
              </div>
            </div>
          </div>

          {/* Tier Selection */}
          <div className="space-y-3" data-id="element-21379">
            {isConsultation ? <>
                {CONSULTATION_TIERS.map(tier => {
              const isActive = selectedConsultationTier === tier.id;
              return <button key={tier.id} onClick={() => setSelectedConsultationTier(tier.id)} className={`flex items-center gap-4 w-full text-left p-4 rounded-xl border-2 transition-all ${isActive ? 'border-sky-500 bg-sky-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-21380">
                      <div className="flex-1 min-w-0" data-id="element-21381">
                        <div className="flex items-center gap-2 mb-1" data-id="element-21382">
                          <span className="font-bold text-slate-900" data-id="element-21383">
                            {tier.title}
                          </span>
                          <Badge variant={tier.badgeVariant} className="text-[10px]" data-id="element-21384">
                            {tier.badge}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-21385">
                          {tier.desc}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2" data-id="element-21386">
                        <div className="text-lg font-bold text-slate-900" data-id="element-21387">
                          {tier.price.toLocaleString('ru-RU')} ₽
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${isActive ? 'border-sky-500 bg-sky-500' : 'border-slate-300'}`} data-id="element-21388">
                        {isActive && <div className="w-2 h-2 rounded-full bg-white" data-id="element-21389" />}
                      </div>
                    </button>;
            })}
              </> : <>
                {/* Free Tier */}
                <button onClick={() => setSelectedPackage('free')} className={`flex items-center gap-4 w-full text-left p-4 rounded-xl border-2 transition-all ${selectedPackage === 'free' ? 'border-indigo-500 bg-indigo-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-21390">
                  <div className="flex-1 min-w-0" data-id="element-21391">
                    <div className="flex items-center gap-2 mb-1" data-id="element-21392">
                      <span className="font-bold text-slate-900" data-id="element-21393">
                        {t('chart.tier.free.title')}
                      </span>
                      <Badge variant="neutral" className="text-[10px]" data-id="element-21394">
                        {t('chart.tier.free.badge')}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-21395">
                      {t('chart.tier.free.desc')}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2" data-id="element-21396">
                    <div className="text-lg font-bold text-slate-900" data-id="element-21397">0 ₽</div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${selectedPackage === 'free' ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`} data-id="element-21398">
                    {selectedPackage === 'free' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-21399" />}
                  </div>
                </button>

                {/* Pro Tier */}
                <button onClick={() => setSelectedPackage('pro')} className={`flex items-center gap-4 w-full text-left p-4 rounded-xl border-2 transition-all ${selectedPackage === 'pro' ? 'border-indigo-500 bg-indigo-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-21400">
                  <div className="flex-1 min-w-0" data-id="element-21401">
                    <div className="flex items-center gap-2 mb-1" data-id="element-21402">
                      <span className="font-bold text-slate-900" data-id="element-21403">
                        {t('chart.tier.pro.title')}
                      </span>
                      <Badge variant="pro" className="text-[10px]" data-id="element-21404">
                        {t('chart.tier.pro.badge')}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-21405">
                      {t('chart.tier.pro.desc')}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2" data-id="element-21406">
                    <div className="text-lg font-bold text-slate-900" data-id="element-21407">
                      {currentService.price.toLocaleString('ru-RU')} ₽
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${selectedPackage === 'pro' ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`} data-id="element-21408">
                    {selectedPackage === 'pro' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-21409" />}
                  </div>
                </button>

                {/* Package Tier */}
                <div className={`rounded-xl border-2 transition-all overflow-hidden ${selectedPackage === 'package' ? 'border-violet-500 bg-violet-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-21410">
                  <button onClick={() => setSelectedPackage('package')} className="flex items-center gap-4 w-full text-left p-4" data-id="element-21411">
                    <div className="flex-1 min-w-0" data-id="element-21412">
                      <div className="flex items-center gap-2 mb-1" data-id="element-21413">
                        <span className="font-bold text-slate-900" data-id="element-21414">
                          {t('chart.tier.package.title')}
                        </span>
                        <Badge variant="success" className="text-[10px]" data-id="element-21415">
                          {t('chart.tier.package.badge')}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-21416">
                        {t('chart.tier.package.desc')}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2" data-id="element-21417">
                      <div className="text-sm font-bold text-violet-600" data-id="element-21418">
                        {t('chart.tier.package.fromPrice')}
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${selectedPackage === 'package' ? 'border-violet-500 bg-violet-500' : 'border-slate-300'}`} data-id="element-21419">
                      {selectedPackage === 'package' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-21420" />}
                    </div>
                  </button>

                  {/* Expandable Package Options */}
                  <AnimatePresence data-id="element-21421">
                    {selectedPackage === 'package' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-violet-100 bg-white/50" data-id="element-21422">
                        <div className="p-4 space-y-2" data-id="element-21423">
                          {[{
                      size: 20 as const,
                      price: 19900,
                      perItem: 995
                    }, {
                      size: 50 as const,
                      price: 44900,
                      perItem: 898,
                      popular: true
                    }, {
                      size: 100 as const,
                      price: 79900,
                      perItem: 799
                    }].map(bundle => <button key={bundle.size} onClick={() => setSelectedBundleSize(bundle.size)} className={`flex items-center justify-between w-full p-3 rounded-lg border transition-all ${selectedBundleSize === bundle.size ? 'border-violet-500 bg-violet-100/50' : 'border-slate-200 bg-white hover:border-violet-300'}`} data-id="element-21424">
                              <div className="flex items-center gap-3" data-id="element-21425">
                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedBundleSize === bundle.size ? 'border-violet-500' : 'border-slate-300'}`} data-id="element-21426">
                                  {selectedBundleSize === bundle.size && <div className="w-2 h-2 rounded-full bg-violet-500" data-id="element-21427" />}
                                </div>
                                <div className="flex items-center gap-2" data-id="element-21428">
                                  <span className="font-bold text-slate-900 text-sm" data-id="element-21429">
                                    {t('chart.bundle.reports', {
                              count: bundle.size.toString()
                            })}
                                  </span>
                                  {bundle.popular && <span className="text-[9px] font-bold text-white bg-violet-500 px-1.5 py-0.5 rounded-full" data-id="element-21430">
                                      {t('chart.bundle.hit')}
                                    </span>}
                                </div>
                              </div>
                              <div className="text-right" data-id="element-21431">
                                <div className="text-sm font-bold text-slate-900" data-id="element-21432">
                                  {bundle.price.toLocaleString('ru-RU')} ₽
                                </div>
                                <div className="text-[10px] text-violet-600" data-id="element-21433">
                                  {bundle.perItem.toLocaleString('ru-RU')} ₽/шт
                                </div>
                              </div>
                            </button>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </>}
          </div>
        </div>

        <div className="flex justify-between pt-6 border-t border-slate-100 mt-8 max-w-2xl mx-auto" data-id="element-21434">
          <Button onClick={handlePrev} variant="outline" icon={<ArrowLeft className="w-4 h-4" data-id="element-21436" />} data-id="element-21435">
            {t('chart.btn.back')}
          </Button>
          <Button onClick={handleNext} variant="primary" icon={<ArrowRight className="w-4 h-4" data-id="element-21438" />} data-id="element-21437">
            {t('chart.btn.next')}
          </Button>
        </div>
      </motion.div>;
  };
  const renderPersonFields = (personKey: 'p1' | 'p2', title?: string) => <div className="space-y-4 bg-slate-50 p-4 sm:p-6 rounded-2xl border border-slate-100" data-id="element-21439">
      {title && <h3 className="font-bold text-slate-900 mb-2" data-id="element-21440">{title}</h3>}
      <Input label={t('chart.field.name')} placeholder={t('chart.field.namePlaceholder')} value={formData[personKey].name} onChange={e => setFormData({
      ...formData,
      [personKey]: {
        ...formData[personKey],
        name: e.target.value
      }
    })} data-id="element-21441" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-21442">
        <Input label={t('chart.field.date')} type="date" required value={formData[personKey].date} onChange={e => setFormData({
        ...formData,
        [personKey]: {
          ...formData[personKey],
          date: e.target.value
        }
      })} data-id="element-21443" />
        <div data-id="element-21444">
          <Input label={t('chart.field.time')} type="time" required value={formData[personKey].time} onChange={e => setFormData({
          ...formData,
          [personKey]: {
            ...formData[personKey],
            time: e.target.value
          }
        })} data-id="element-21445" />
          <p className="text-[10px] sm:text-xs text-slate-500 mt-1" data-id="element-21446">
            {t('chart.field.timeHint')}
          </p>
        </div>
      </div>
      <Input label={t('chart.field.city')} placeholder={t('chart.field.cityPlaceholder')} required icon={<MapPin className="w-4 h-4" data-id="element-21448" />} value={formData[personKey].city} onChange={e => setFormData({
      ...formData,
      [personKey]: {
        ...formData[personKey],
        city: e.target.value
      }
    })} data-id="element-21447" />
    </div>;
  const renderStep3 = () => {
    const CurrentIcon = currentService.icon;
    return <motion.div key="step3" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="space-y-6" data-id="element-21449">
        <div className="text-center mb-6" data-id="element-21450">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-21451">
            {t('chart.step3.title')}
          </h2>
          <p className="text-slate-500 text-sm sm:text-base" data-id="element-21452">
            {t('chart.step3.subtitle', {
            name: currentService.title
          })}
          </p>
        </div>

        <div className="max-w-2xl mx-auto mb-6" data-id="element-21453">
          <div className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-indigo-50/50 border border-indigo-100" data-id="element-21454">
            <div className="flex items-center gap-3" data-id="element-21455">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white shadow-sm flex-shrink-0 ${currentService.color}`} data-id="element-21456">
                <CurrentIcon className="w-4 h-4" data-id="element-21457" />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2" data-id="element-21458">
                <span className="text-sm font-bold text-slate-900" data-id="element-21459">
                  {currentService.title}
                </span>
                <span className="hidden sm:inline text-slate-300" data-id="element-21460">•</span>
                <span className="text-xs font-medium text-slate-600" data-id="element-21461">
                  {selectedService === 'consultation' ? CONSULTATION_TIERS.find(t => t.id === selectedConsultationTier)?.title : selectedPackage === 'free' ? t('chart.tier.free.title') : selectedPackage === 'pro' ? t('chart.tier.pro.title') : t('chart.bundle.reports', {
                  count: selectedBundleSize.toString()
                })}
                </span>
              </div>
            </div>
            <div className="text-right" data-id="element-21462">
              <div className="text-sm font-bold text-indigo-600" data-id="element-21463">
                {selectedService === 'consultation' ? `${(CONSULTATION_TIERS.find(t => t.id === selectedConsultationTier)?.price || 4990).toLocaleString('ru-RU')} ₽` : selectedPackage === 'free' ? '0 ₽' : selectedPackage === 'pro' ? `${currentService.price.toLocaleString('ru-RU')} ₽` : `${(selectedBundleSize === 20 ? 19900 : selectedBundleSize === 50 ? 44900 : 79900).toLocaleString('ru-RU')} ₽`}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleCalculate} className="space-y-6 max-w-2xl mx-auto" data-id="element-21464">
          {selectedService === 'compatibility' ? <div className="space-y-6" data-id="element-21465">
              {renderPersonFields('p1', t('chart.field.person1'))}
              {renderPersonFields('p2', t('chart.field.person2'))}
            </div> : <div className="space-y-6" data-id="element-21466">
              {renderPersonFields('p1')}

              {selectedService === 'solar' && <div className="bg-amber-50/50 p-4 sm:p-6 rounded-2xl border border-amber-100" data-id="element-21467">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-21468">
                    {t('chart.field.forecastYear')}
                  </label>
                  <select className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" value={formData.year} onChange={e => setFormData({
              ...formData,
              year: e.target.value
            })} data-id="element-21469">
                    {[0, 1, 2, 3].map(offset => {
                const year = new Date().getFullYear() + offset;
                return <option key={year} value={year} data-id="element-21470">
                          {year}
                        </option>;
              })}
                  </select>
                </div>}

              {selectedService === 'forecast' && <div className="bg-emerald-50/50 p-4 sm:p-6 rounded-2xl border border-emerald-100" data-id="element-21471">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-21472">
                    {t('chart.field.forecastPeriod')}
                  </label>
                  <select className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" value={formData.period} onChange={e => setFormData({
              ...formData,
              period: e.target.value
            })} data-id="element-21473">
                    <option value="3" data-id="element-21474">{t('chart.period.3months')}</option>
                    <option value="6" data-id="element-21475">{t('chart.period.6months')}</option>
                    <option value="12" data-id="element-21476">{t('chart.period.1year')}</option>
                  </select>
                </div>}
            </div>}

          <div className="flex justify-between pt-6 border-t border-slate-100 mt-8" data-id="element-21477">
            <Button type="button" onClick={handlePrev} variant="outline" icon={<ArrowLeft className="w-4 h-4" data-id="element-21479" />} data-id="element-21478">
              {t('chart.btn.back')}
            </Button>
            <Button type="submit" variant="primary" icon={selectedService === 'consultation' ? <ArrowRight className="w-4 h-4" data-id="element-21481" /> : <Zap className="w-4 h-4" data-id="element-21482" />} data-id="element-21480">
              {selectedService === 'consultation' ? t('chart.btn.book') : t('chart.btn.calculate')}
            </Button>
          </div>
        </form>
      </motion.div>;
  };
  const renderStep4 = () => {
    if (isCalculating) {
      return <motion.div key="calculating" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex flex-col items-center justify-center py-20 text-center space-y-6" data-id="element-21483">
          <div className="relative w-24 h-24" data-id="element-21484">
            <div className="absolute inset-0 border-4 border-slate-100 rounded-full" data-id="element-21485"></div>
            <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin" data-id="element-21486"></div>
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-21487">
              <currentService.icon className={`w-8 h-8 ${currentService.color}`} data-id="element-21488" />
            </div>
          </div>
          <div data-id="element-21489">
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-21490">
              {t('chart.calculating.title')}
            </h2>
            <p className="text-slate-500" data-id="element-21491">{t('chart.calculating.subtitle')}</p>
          </div>
        </motion.div>;
    }
    return <motion.div key="result" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-6" data-id="element-21492">
        {/* New calculation button */}
        <div className="flex items-center justify-between" data-id="element-21493">
          <div data-id="element-21494">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-21495">
              {selectedPackage === 'pro' ? t('chart.result.fullReport') : t('chart.result.basicReport')}
              : {currentService.title}
            </h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-21496">
              {formData.p1.name && `${formData.p1.name} • `}
              {formData.p1.date || '01.01.1990'} •{' '}
              {formData.p1.city || 'Москва'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={resetWizard} icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-21498" />} data-id="element-21497">
            {t('chart.result.newCalc')}
          </Button>
        </div>

        {/* Render the appropriate report preview */}
        {selectedPackage === 'free' ? <>
            <Card padding="md" className="border-slate-200 mb-8" data-id="element-21499">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-21500">
                <Star className="w-5 h-5 text-indigo-500" data-id="element-21501" />
                {t('chart.result.planets')}
              </h3>
              <div className="overflow-hidden rounded-xl border border-slate-100" data-id="element-21502">
                <table className="w-full text-sm text-left" data-id="element-21503">
                  <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-21504">
                    <tr data-id="element-21505">
                      <th className="px-4 py-3" data-id="element-21506">{t('chart.result.planet')}</th>
                      <th className="px-4 py-3" data-id="element-21507">{t('chart.result.sign')}</th>
                      <th className="px-4 py-3 text-right" data-id="element-21508">
                        {t('chart.result.degree')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-21509">
                    {planetPositions.map((planet, i) => <tr key={i} className="hover:bg-slate-50/50 transition-colors" data-id="element-21510">
                        <td className="px-4 py-3 flex items-center gap-2 font-medium text-slate-900" data-id="element-21511">
                          {planet.icon}
                          {planet.name}
                        </td>
                        <td className="px-4 py-3 text-slate-600" data-id="element-21512">
                          {planet.sign}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500 font-mono text-xs" data-id="element-21513">
                          {planet.degree}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="flex items-center gap-4 mb-8" data-id="element-21514">
              <div className="h-px bg-slate-200 flex-1" data-id="element-21515" />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21516">
                {t('chart.result.briefInterpretation')}
              </span>
              <div className="h-px bg-slate-200 flex-1" data-id="element-21517" />
            </div>

            <BetaReportPreview serviceType={selectedService as 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast'} onNavigate={onNavigate} data-id="element-21518" />

            <div className="mt-8 space-y-4" data-id="element-21519">
              <Card padding="lg" className="bg-gradient-to-br from-indigo-900 to-slate-900 border-indigo-800 text-white shadow-xl shadow-indigo-900/20" data-id="element-21520">
                <div className="flex flex-col sm:flex-row items-center gap-6" data-id="element-21521">
                  <div className="flex-1 text-center sm:text-left" data-id="element-21522">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-bold mb-3 border border-indigo-500/30" data-id="element-21523">
                      <Sparkles className="w-3.5 h-3.5" data-id="element-21524" /> Premium
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2" data-id="element-21525">
                      {t('chart.result.wantFull')}
                    </h3>
                    <p className="text-indigo-200 text-sm leading-relaxed" data-id="element-21526">
                      {t('chart.result.fullDesc')}
                    </p>
                  </div>
                  <div className="w-full sm:w-auto flex-shrink-0" data-id="element-21527">
                    <Button variant="primary" size="lg" fullWidth className="bg-indigo-500 hover:bg-indigo-400 text-white shadow-lg shadow-indigo-500/25" onClick={() => {
                  const priceMap: Record<string, number> = {
                    natal: 3900,
                    solar: 3900,
                    compatibility: 4900,
                    combo: 6900,
                    forecast: 3900
                  };
                  addItem({
                    id: `premium-${selectedService}-${Date.now()}`,
                    name: `Premium отчёт: ${currentService.title}`,
                    description: currentService.desc,
                    price: priceMap[selectedService] || 3900,
                    type: 'report',
                    format: 'PDF-отчёт',
                    badges: [{
                      label: 'Premium',
                      variant: 'pro'
                    }]
                  });
                  onNavigate('checkout');
                }} data-id="element-21528">
                      {t('chart.result.getPremium')}
                    </Button>
                  </div>
                </div>
              </Card>

              <Card padding="md" className="border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-21529">
                <div className="flex items-center gap-3" data-id="element-21530">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-sky-500" data-id="element-21531">
                    <MessageCircle className="w-5 h-5" data-id="element-21532" />
                  </div>
                  <div data-id="element-21533">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-21534">
                      {t('chart.result.questions')}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-21535">
                      {t('chart.result.consultDesc')}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full sm:w-auto bg-white" onClick={() => onNavigate('service-consultation')} data-id="element-21536">
                  {t('chart.result.chooseAstrologer')}
                </Button>
              </Card>
            </div>
          </> : <PremiumReportPreview serviceType={selectedService as 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast'} data-id="element-21537" />}
      </motion.div>;
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-21538">
      <div className="bg-white border-b border-slate-200 pt-8 pb-12" data-id="element-21539">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21540">
          <Breadcrumbs items={[{
          label: t('nav.services'),
          onClick: () => onNavigate('home')
        }, {
          label: t('chart.breadcrumb'),
          onClick: resetWizard
        }]} className="mb-8" data-id="element-21541" />

          <div className="text-center mb-10" data-id="element-21542">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-21543">
              {t('chart.title')}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-6" data-id="element-21544">
              {t('chart.subtitle')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6" data-id="element-21545">
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-full" data-id="element-21546">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-21547" />
                {t('chart.rating')}
              </div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-full" data-id="element-21548">
                <Zap className="w-4 h-4 text-indigo-500" data-id="element-21549" />
                {t('chart.calculations')}
              </div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-full" data-id="element-21550">
                <ShieldCheck className="w-4 h-4 text-emerald-500" data-id="element-21551" />
                {t('chart.freeBeta')}
              </div>
            </div>
          </div>

          {/* Stepper only visible during steps 1-3 */}
          {step < 4 && renderStepper()}
        </div>
      </div>

      <div className={`mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-12 ${step === 4 ? 'max-w-7xl' : 'max-w-3xl'}`} data-id="element-21552">
        <AnimatePresence mode="wait" data-id="element-21553">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && <div className="space-y-8" data-id="element-21554">
              {renderStep4()}
              <AdSlot slotId="SLOT-08" onNavigate={onNavigate} data-id="element-21555" />
            </div>}
        </AnimatePresence>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24" data-id="element-21556">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6" data-id="element-21557">
          <Card padding="md" className="bg-white border-slate-100 text-center" data-id="element-21558">
            <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4" data-id="element-21559">
              <ShieldCheck className="w-6 h-6 text-indigo-500" data-id="element-21560" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-21561">
              {t('chart.trust.accurate.title')}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-21562">
              {t('chart.trust.accurate.desc')}
            </p>
          </Card>
          <Card padding="md" className="bg-white border-slate-100 text-center" data-id="element-21563">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4" data-id="element-21564">
              <Lock className="w-6 h-6 text-emerald-500" data-id="element-21565" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-21566">
              {t('chart.trust.privacy.title')}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-21567">
              {t('chart.trust.privacy.desc')}
            </p>
          </Card>
          <Card padding="md" className="bg-white border-slate-100 text-center" data-id="element-21568">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4" data-id="element-21569">
              <Zap className="w-6 h-6 text-amber-500" data-id="element-21570" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-21571">
              {t('chart.trust.instant.title')}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-21572">
              {t('chart.trust.instant.desc')}
            </p>
          </Card>
        </div>
      </div>
    </div>;
}